/* eslint-disable react-hooks/exhaustive-deps */
import { useRef, useEffect } from "react";
import maplibregl from "maplibre-gl";
import "maplibre-gl/dist/maplibre-gl.css";
import { syncTransportLines } from "../utils/mapTransportLine";
import { createMapMarker } from "../utils/mapMarker";
import { getViewportForSegments } from "../utils/mapViewportUtils";
import {
  buildMarkerGroupsFromSegments,
  buildClusterGroupsFromMarkerGroups,
} from "../utils/mapMarkerGroups";
// import { clusterMarkerGroups } from "../utils/mapMarkerCluster";
import {
  autoPrecisionForZoom,
  zoomToPrecision,
} from "../utils/mapViewportUtils";
import { createClusterMarker } from "../utils/mapMarkerCluster";

/** Optional: tweak defaults here */
const DEFAULT_VIEW = {
  center: [-46, 116],
  zoom: 8,
  bearing: 0,
  pitch: 0,
};

/** Helper so cleanup works whether we hold wrappers or raw markers */
function cleanupMarker(mk) {
  if (!mk) return;
  if (typeof mk.destroy === "function") {
    try {
      mk.destroy();
    } catch {}
    return;
  }
  if (typeof mk.remove === "function") {
    try {
      mk.remove();
    } catch {}
    return;
  }
  if (mk.marker?.remove) {
    try {
      mk.marker.remove();
    } catch {}
  }
}

export default function MapComponent({
  /** Pass your itinerary segments array here */
  segments = [],
  /** Keep this inline height — it’s what fixed rendering in your app */
  mapHeight = 634,
  /** Optionally override the view / style */
  view = DEFAULT_VIEW,
  styleUrl = "https://tiles.openfreemap.org/styles/bright",
  /** Transportation Lines */
  showLines = false,
  lineStyle = { color: "#2563eb", width: 1, opacity: 0.5 }, //, dasharray: [2, 2] },
}) {
  const mapRef = useRef(null);
  const mapDivRef = useRef(null);
  const markersRef = useRef([]); // for individual (de-duped) markers
  const clusterMarkersRef = useRef([]); // for cluster markers
  const markerGroupsRef = useRef([]); // latest de-duped markerGroups, shared with moveend

  // registry for line handles
  const linesRef = useRef(new Map());

  // --- init once
  useEffect(() => {
    if (mapRef.current || !mapDivRef.current) return;

    const view = getViewportForSegments(segments);

    const map = new maplibregl.Map({
      container: mapDivRef.current,
      style: styleUrl,
      // center: view.center,
      // zoom: view.zoom,
      bearing: view.bearing ?? 0,
      pitch: view.pitch ?? 0,
      attributionControl: false,
      ...view,
    });

    mapRef.current = map;

    // Controls
    map.addControl(
      new maplibregl.NavigationControl({ showCompass: false }),
      "top-left"
    );
    map.addControl(
      new maplibregl.ScaleControl({ maxWidth: 140, unit: "imperial" }),
      "bottom-left"
    );

    // Resize once painted + on container changes
    const rafId = requestAnimationFrame(() => {
      try {
        map.resize();
      } catch {}
    });
    const ro = new ResizeObserver(() => {
      try {
        map.resize();
      } catch {}
    });
    ro.observe(mapDivRef.current);

    // map.on("zoom", () => {
    //   // Get the current zoom level
    //   const zoom = map.getZoom();
    //   console.debug(zoom);
    //   // const zoomElement = document.getElementById("zoom-level");
    //   // zoomElement.textContent = `Zoom: ${zoom.toFixed(2)}`; // Use toFixed() for a clean number
    // });

    map.on("error", (e) => console.error("[MapLibre error]", e?.error || e));

    return () => {
      // cancelAnimationFrame(rafId);
      // ro.disconnect();
      // // remove any markers we added
      // markersRef.current.forEach(cleanupMarker);
      // markersRef.current = [];
      // map.remove();
      // mapRef.current = null;

      cancelAnimationFrame(rafId);
      ro.disconnect();

      // remove any individual markers
      markersRef.current.forEach(cleanupMarker);
      markersRef.current = [];

      // NEW: remove any cluster markers
      clusterMarkersRef.current.forEach((w) => {
        try {
          w.destroy?.(); // if you use same wrapper shape as createMapMarker
        } catch {}
      });
      clusterMarkersRef.current = [];

      map.remove();
      mapRef.current = null;
    };
  }, []); // init once

  // --- (re)build markers when segments change
  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;

    const { center, zoom } = getViewportForSegments(segments);

    // 1) Build and store de-duplicated markerGroups for this set of segments
    const markerGroups = buildMarkerGroupsFromSegments(segments || []);
    markerGroupsRef.current = markerGroups;

    // 2) Helper to clear all markers (individual + clusters)
    function clearAllMarkers() {
      // individual markers
      markersRef.current.forEach((w) => {
        try {
          w.destroy?.();
        } catch {}
      });
      markersRef.current = [];

      // cluster markers
      clusterMarkersRef.current.forEach((w) => {
        try {
          w.destroy?.();
        } catch {}
      });
      clusterMarkersRef.current = [];
    }

    // 3) Main cluster refresh logic (depends on current zoom)
    function refreshMarkersForZoom() {
      const z = map.getZoom();
      // const precision = zoomToPrecision(z);  //previous version
      const precision = autoPrecisionForZoom(z);

      clearAllMarkers();

      const groups = markerGroupsRef.current || [];
      const clusters = buildClusterGroupsFromMarkerGroups(groups, precision);

      for (const cluster of clusters) {
        if (cluster.members.length === 1) {
          // Single de-duped location → standard marker
          const mg = cluster.members[0];
          const seg =
            mg.anchorSegment || (mg.points[0] && mg.points[0].segment);
          if (!seg) continue;

          const wrappers = createMapMarker({
            map,
            segment: seg,
            markerGroup: mg,
            draggable: true,
            addPopup: true,
            point: "single",
          });

          markersRef.current.push(...wrappers);
        } else {
          // Multiple overlapping locations → cluster marker with advanced popup
          const clusterWrapper = createClusterMarker({
            map,
            cluster,
            onSelectMarkerGroup: (selectedGroup) => {
              // User picked a specific markerGroup from the cluster popup
              const targetLng = selectedGroup.lng;
              const targetLat = selectedGroup.lat;

              const targetZoom = Math.max(map.getZoom() + 3, 12);

              map.flyTo({
                center: [targetLng, targetLat],
                zoom: targetZoom,
                duration: 600,
                essential: true,
              });

              // After we finish zooming, re-cluster (will likely break into
              // individual markers) and open the popup for the chosen marker.
              map.once("moveend", () => {
                refreshMarkersForZoom();

                const wrapper = markersRef.current.find(
                  (w) =>
                    w.markerGroup?.key === selectedGroup.key ||
                    (w.marker.getLngLat().lng === targetLng &&
                      w.marker.getLngLat().lat === targetLat)
                );

                try {
                  wrapper?.marker?.togglePopup?.();
                } catch {}
              });
            },
          });

          if (clusterWrapper) {
            clusterMarkersRef.current.push(clusterWrapper);
          }
        }
      }
    }

    // 4) Initial flyTo and first placement of markers/clusters
    map.flyTo({
      center,
      zoom,
      duration: 500,
      essential: true,
    });

    refreshMarkersForZoom();

    // 5) Rebuild clusters when the user pans / zooms (re-group markers)
    const handleMoveEnd = () => {
      refreshMarkersForZoom();
    };

    map.on("moveend", handleMoveEnd);

    // 6) Cleanup for this effect
    return () => {
      map.off("moveend", handleMoveEnd);
      clearAllMarkers();
    };
  }, [segments]);

  // previous
  // useEffect(() => {
  //   const map = mapRef.current;
  //   if (!map) return;

  //   const { center, zoom } = getViewportForSegments(segments);

  //   // clear previous markers safely
  //   const prev = markersRef.current;
  //   markersRef.current = [];
  //   prev.forEach(cleanupMarker);

  //   // NEW: build de-duplicated marker groups
  //   const markerGroups = buildMarkerGroupsFromSegments(segments || []);

  //   // add new — IMPORTANT: flatMap (createMapMarker can return 1..N)
  //   // const added = (segments || []).flatMap((segment) =>
  //   const added = markerGroups.flatMap((group) =>
  //     createMapMarker({
  //       map,
  //       segment:
  //         group.anchorSegment || (group.points[0] && group.points[0].segment),
  //       draggable: true, //segment?.draggable ?? true,
  //       addPopup: true, //segment?.popup ?? true, // true => default popup; object => custom
  //       // we no longer need "point: 'both'" – we already computed a single
  //       // physical point for this marker:
  //       point: "single",
  //       // previous
  //       // point:
  //       //   segment?.point ??
  //       //   // ((segment?.type || "").toLowerCase() === "transportation"
  //       //   (Object.keys(segment.coordinates).length === 2 ? "both" : "primary"), // if coordinates has two keys, it has two points
  //       // onDragEnd: segment?.onDragEnd,

  //       // iconOverride: { name: "star", size: 24, className: "text-yellow-500" }, // optional per-segment override

  //       // OPTIONAL: give createMapMarker more context so popup
  //       // can show both departures & arrivals for this location:
  //       markerGroup: group, // <-- new field
  //     })
  //   );
  //   console.debug(added);
  //   markersRef.current = added;

  //   map.flyTo({
  //     center,
  //     zoom,
  //     duration: 500,
  //     essential: true,
  //   });

  //   return () => {
  //     markersRef.current.forEach(cleanupMarker);
  //     markersRef.current = [];
  //   };
  // }, [segments]);

  // transport lines effect
  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;

    linesRef.current = syncTransportLines({
      map,
      segments,
      visible: showLines,
      style: lineStyle,
      registry: linesRef.current,
    });

    // Cleanup on deps change / unmount
    return () => {
      // if turning off, or segments changed drastically, we still clean
      linesRef.current.forEach((h) => {
        try {
          h.remove();
        } catch {}
      });
      linesRef.current.clear();
    };
  }, [segments, showLines, lineStyle]);

  const handleRecenter = () => {
    const { center, zoom } = getViewportForSegments(segments);
    const map = mapRef.current;
    if (!map) return;
    map.flyTo({
      center: center,
      zoom: zoom,
      // bearing: view.bearing ?? 0,
      // pitch: view.pitch ?? 0,
      duration: 700,
      essential: true,
    });
  };

  return (
    <div className="map-frame" style={{ position: "relative" }}>
      {/* The inline size is key in your app */}
      <div
        ref={mapDivRef}
        className="map-canvas"
        aria-label="Map"
        style={{ width: "100%", height: mapHeight }}
      />
      <button
        type="button"
        className="map-recenter-btn"
        onClick={handleRecenter}
        aria-label="Recenter map"
        title="Recenter"
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="22"
          height="22"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.75"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <path d="M12 8a4 4 0 1 0 4 4" />
          <path d="M12 2v3M12 19v3M2 12h3M19 12h3" />
          <circle cx="12" cy="12" r="2" />
        </svg>
      </button>
    </div>
  );
}
/* eslint-enable react-hooks/exhaustive-deps */
